import { SOCIAL_PLATFORMS, SOCIAL_TRACKER_CADENCES, type SocialSyncSchedule, type SocialUniverseItem } from '@digital-twins/shared-types'
import type { SocialTopicListFacet, SocialTopicListFilters, SocialTopicListOptions, SocialTopicListSort, SocialTopicListStatus } from '@/types/components/social/topicListFilters'
import { effectiveSocialCadence } from './socialFormat'
import { universeStatusTone } from './socialTrackerStatus'

export const TOPIC_LIST_FACETS: Array<SocialTopicListFacet> = ['market', 'brand', 'theme', 'channel', 'status', 'language', 'cadence']
export const TOPIC_LIST_SORTS: Array<SocialTopicListSort> = ['updated', 'posts', 'reach', 'growth']
const STATUSES: Array<SocialTopicListStatus> = ['ready', 'updating', 'paused', 'attention']
export const TOPIC_LIST_PARAMS = {
  q: 'socialQ', sort: 'socialSort', market: 'socialMarket', brand: 'socialBrand', theme: 'socialTheme',
  channel: 'socialChannel', status: 'socialStatus', language: 'socialLanguage', cadence: 'socialCadence',
} as const

export function topicListStatus(topic: SocialUniverseItem): SocialTopicListStatus {
  const tone = universeStatusTone(topic.channels)
  if (tone === 'progress') return 'updating'
  if (tone === 'muted') return 'paused'
  return tone === 'positive' ? 'ready' : 'attention'
}

/** Literal configured metadata, never inferred from topic prose or local tag IDs. */
export function topicListValues(topic: SocialUniverseItem, facet: SocialTopicListFacet, schedule: SocialSyncSchedule = 'daily'): Array<string> {
  switch (facet) {
    case 'market': return [topic.market]
    case 'brand': return topic.brands.map(brand => brand.name)
    case 'theme': return topic.topics.map(theme => theme.name)
    case 'channel': return topic.channels.map(channel => channel.platform)
    case 'status': return [topicListStatus(topic)]
    case 'language': return topic.languages
    case 'cadence': return [effectiveSocialCadence(topic.cadence, schedule)]
  }
}

export function topicListFiltersFromParams(params: URLSearchParams): SocialTopicListFilters {
  const value = (key: SocialTopicListFacet) => params.get(TOPIC_LIST_PARAMS[key]) || null
  const sort = params.get(TOPIC_LIST_PARAMS.sort)
  const valid = (item: string | null, allowed: ReadonlyArray<string>) => item && allowed.includes(item) ? item : null
  return {
    q: params.get(TOPIC_LIST_PARAMS.q) ?? '',
    sort: TOPIC_LIST_SORTS.includes(sort as SocialTopicListSort) ? sort as SocialTopicListSort : 'updated',
    market: value('market'), brand: value('brand'), theme: value('theme'), language: value('language'),
    channel: valid(value('channel'), SOCIAL_PLATFORMS), status: valid(value('status'), STATUSES),
    cadence: valid(value('cadence'), SOCIAL_TRACKER_CADENCES),
  }
}

export function topicListHasFilters(filters: SocialTopicListFilters): boolean {
  return Boolean(filters.q.trim()) || TOPIC_LIST_FACETS.some(key => filters[key] !== null)
}

export function topicListOptions(topics: ReadonlyArray<SocialUniverseItem>, filters?: SocialTopicListFilters, schedule: SocialSyncSchedule = 'daily'): SocialTopicListOptions {
  return Object.fromEntries(TOPIC_LIST_FACETS.map(key => {
    const counts = new Map<string, number>()
    for (const topic of topics) {
      const available = !filters || matchesTopic(topic, filters, schedule, key)
      for (const value of new Set(topicListValues(topic, key, schedule).filter(value => value.trim() !== ''))) {
        counts.set(value, (counts.get(value) ?? 0) + (available ? 1 : 0))
      }
    }
    return [key, [...counts].sort(([a], [b]) => a.localeCompare(b)).map(([value, count]) => ({ value, count }))]
  })) as SocialTopicListOptions
}

function knownNumber(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}
function timestamp(value: string | null): number | null {
  return value ? knownNumber(Date.parse(value)) : null
}
function metric(topic: SocialUniverseItem, sort: SocialTopicListSort): number | null {
  // A Topic that has not finished an update sorts by when it was created, so a new Topic comes first (D22).
  if (sort === 'updated') return timestamp(topic.lastSyncedAt) ?? timestamp(topic.createdAt)
  if (sort === 'posts') return knownNumber(topic.totalPosts)
  if (sort === 'reach') return knownNumber(topic.reach)
  const recent = knownNumber(topic.postsLast7Days)
  const previous = knownNumber(topic.postsPrevious7Days)
  return recent !== null && previous !== null ? recent - previous : null
}
function descending(a: number | null, b: number | null): number {
  if (a === b) return 0
  if (a === null) return 1
  if (b === null) return -1
  return b - a
}

function matchesTopic(topic: SocialUniverseItem, filters: SocialTopicListFilters, schedule: SocialSyncSchedule, except?: SocialTopicListFacet): boolean {
  const query = filters.q.trim().toLocaleLowerCase()
  return (!query || [topic.name, ...topic.keywords].join(' ').toLocaleLowerCase().includes(query))
    && TOPIC_LIST_FACETS.every(key => key === except || filters[key] === null || topicListValues(topic, key, schedule).includes(filters[key]!))
}

export function filterTopicList(topics: ReadonlyArray<SocialUniverseItem>, filters: SocialTopicListFilters, schedule: SocialSyncSchedule = 'daily'): Array<SocialUniverseItem> {
  return topics.filter(topic => matchesTopic(topic, filters, schedule))
    .sort((a, b) => descending(metric(a, filters.sort), metric(b, filters.sort))
      || descending(timestamp(a.createdAt), timestamp(b.createdAt)) || a.token.localeCompare(b.token))
}
